// FriendsOnlineDock.js — Languette flottante « Mes amis en ligne » (comme « Mes groupes »), montée APP-WIDE.
// Visible seulement s'il y a ≥1 ami EN LIGNE. Où qu'on soit dans l'app : consulter ses amis présents + agir
// (profil, message, inviter dans un groupe). Présence ROBUSTE (ping serveur + grâce 60 s anti-clignotement),
// alignée sur la logique de PresenceContext / LobbyGroups. Responsive : mêmes règles que le dock des groupes.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { FiUserCheck, FiChevronUp, FiChevronDown, FiX, FiUser, FiMessageCircle, FiUserPlus } from 'react-icons/fi';
import { theme } from '../theme';
import { useAuth } from '../context/AuthContext';
import { openChatWindow } from '../services/chatWindows';
import { onlineFriendIds } from '../services/presence';
import { labelFromStatus, deviceFromStatus } from '../services/activity';
import LevelBadge from './LevelBadge';
import ProfileCard from './ProfileCard';

// Languette flottante « Mes amis en ligne » — DESKTOP uniquement (sur mobile, regroupée dans le drawer social,
// cf. SocialDock). Empilée à gauche/au-dessus de « Mes groupes ».
const Dock = styled.div`
  position:fixed;z-index:1499;font-family:${theme.fonts.body};
  display:none;                                               /* mobile : caché → présent dans le drawer social */
  @media (min-width:768px){ display:block; right:28rem; bottom:4.7rem; }   /* desktop étroit : au-dessus de « Mes groupes » (aligné sur 28rem) */
  @media (min-width:1100px){ right:41rem; bottom:1.2rem; }  /* desktop large : à GAUCHE de « Mes groupes » (28rem), même rangée */
`;
// Mode « drawer » : section pleine largeur, toujours dépliée (dans le drawer social mobile).
const Section = styled.div`font-family:${theme.fonts.body};`;
const SecHead = styled.div`display:flex;align-items:center;gap:.4rem;padding:.5rem .2rem;font-weight:700;font-size:.9rem;
  color:${theme.colors.textPrimary};border-bottom:1px solid ${theme.colors.border};`;
const Bar = styled.button`display:inline-flex;align-items:center;gap:.5rem;white-space:nowrap;cursor:pointer;position:relative;
  background:${theme.colors.bgCard};color:${theme.colors.textPrimary};border:1px solid ${theme.colors.borderHover};border-radius:999px;
  padding:.6rem 1rem;font-weight:700;font-size:.86rem;box-shadow:${theme.shadows.card};
  .n{display:inline-flex;align-items:center;gap:.5rem;} .dot{width:8px;height:8px;border-radius:50%;background:#22c55e;box-shadow:0 0 6px #22c55e;}
  .b{background:rgba(34,197,94,.18);color:#22c55e;border-radius:999px;padding:0 .45rem;font-size:.72rem;font-weight:800;}
  /* Badge d'angle : NOMBRE d'amis en ligne, toujours visible AVANT ouverture (vert). */
  .cnt{position:absolute;top:-6px;right:-6px;min-width:18px;height:18px;padding:0 4px;border-radius:999px;background:#22c55e;color:#04120a;
    font-size:.68rem;font-weight:800;display:inline-flex;align-items:center;justify-content:center;box-shadow:0 0 0 2px ${theme.colors.bgPrimary},0 0 6px #22c55e;}`;
const Panel = styled.div`position:absolute;bottom:calc(100% + .5rem);right:0;width:min(320px,86vw);
  background:${theme.colors.bgCard};border:1px solid ${theme.colors.borderHover};
  border-radius:14px;box-shadow:${theme.shadows.card};max-height:min(70vh,520px);overflow:hidden;display:flex;flex-direction:column;`;
const Head = styled.div`display:flex;align-items:center;justify-content:space-between;gap:.5rem;padding:.6rem .8rem;
  border-bottom:1px solid ${theme.colors.border};color:${theme.colors.textPrimary};font-weight:700;font-size:.88rem;
  button{background:none;border:none;color:${theme.colors.textMuted};cursor:pointer;display:inline-flex;}`;
const List = styled.div`overflow-y:auto;padding:.35rem;`;
/* Carte VERTICALE (3 lignes) → chaque info a sa place, rien n'est rogné :
   L1 = pastille + pseudo (prend toute la largeur) + device 📱/💻 + niveau ;
   L2 = boutons (profil / message / inviter) ;
   L3 = statut EN ENTIER (retour à la ligne si besoin). */
const Row = styled.div`display:flex;flex-direction:column;gap:.3rem;padding:.5rem .55rem;border-radius:${theme.radii.md};font-size:.84rem;
  &:hover{background:${theme.colors.bgPrimary};}
  .topline{display:flex;align-items:center;gap:.4rem;}
  .dot{width:8px;height:8px;border-radius:50%;flex-shrink:0;}
  .nmtxt{flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:${theme.colors.textPrimary};font-weight:600;}
  .dev{flex-shrink:0;font-size:.92em;}
  .actions{display:flex;gap:.45rem;padding-left:1.1rem;}
  .st{font-size:.72rem;line-height:1.25;padding-left:1.1rem;white-space:normal;overflow-wrap:anywhere;}`;
const Ico = styled.button`background:none;border:1px solid ${theme.colors.border};color:${theme.colors.textSecondary};
  border-radius:999px;cursor:pointer;padding:.22rem .4rem;display:inline-flex;align-items:center;flex-shrink:0;
  &:hover{border-color:${theme.colors.borderHover};color:${theme.colors.textPrimary};}`;
const Empty = styled.p`color:${theme.colors.textMuted};font-size:.82rem;text-align:center;padding:1rem;`;
const Msg = styled.p`color:${theme.colors.primaryLight};font-size:.76rem;padding:.2rem .8rem .5rem;`;

export default function FriendsOnlineDock({ mode = 'float', onCount }) {
  const { auth, isLoggedIn } = useAuth();
  const [friends, setFriends] = useState([]);           // amis acceptés [{user}]
  const [fOnline, setFOnline] = useState({});           // uid -> label (En ligne / Inactif… )
  const seenRef = useRef({});                           // uid -> dernier instant vu présent (grâce 60 s)
  const devRef = useRef({});                            // uid -> '📱' | '💻' (type d'appareil, lu du statut Nakama)
  const [open, setOpen] = useState(false);
  const [card, setCard] = useState(null);
  const [msg, setMsg] = useState('');
  const [myGroup, setMyGroup] = useState(null);

  const load = useCallback(async () => {
    if (!isLoggedIn || !auth.enabled) { setFriends([]); setFOnline({}); return; }
    try {
      const fr = await auth.listFriends(0);
      setFriends(Array.isArray(fr) ? fr : []);
      const fids = (fr || []).map((f) => f.user && (f.user.id || f.user.user_id)).filter(Boolean);
      if (!fids.length) { setFOnline({}); return; }
      const ids = await onlineFriendIds();   // Array = succès (même vide) ; null = RPC échoué → on ne touche à rien
      if (Array.isArray(ids)) {
        const now = Date.now(), seen = seenRef.current, fset = new Set(fids);
        ids.forEach((id) => { if (fset.has(id)) seen[id] = now; });
        let statusById = {};
        try {
          const pres = await auth.followUsers(fids);
          // followUsers = présences ACTUELLES → un ami est en ligne MÊME s'il ne « pingue » pas (onglet en arrière-plan).
          (pres || []).forEach((p) => { const id = p && p.user_id; if (id && fset.has(id)) { seen[id] = now; devRef.current[id] = deviceFromStatus(p.status) || devRef.current[id] || ''; } });
          statusById = Object.fromEntries((pres || []).map((p) => [p.user_id, labelFromStatus(p.status)]));
        } catch (e) { /* */ }
        setFOnline((prev) => { const next = {}; Object.keys(seen).forEach((id) => { if (now - seen[id] < 60000) next[id] = statusById[id] || prev[id] || 'En ligne'; else delete seen[id]; }); return next; });
      }
      try { const mine = await auth.listMyGroups(); const g = (mine || []).find((x) => x.canManage || x.edge_count != null); if (g) setMyGroup({ id: g.id, name: g.name }); } catch (e) { /* */ }
    } catch (e) { /* */ }
  }, [auth, isLoggedIn]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const iv = setInterval(load, 8000); return () => clearInterval(iv); }, [load]);
  // Socket : ajoute/enrichit en instantané (join) ; ne retire JAMAIS (le poll ping décide du hors-ligne).
  useEffect(() => {
    if (!isLoggedIn || !auth.onStatusPresence) return undefined;
    const off = auth.onStatusPresence((ev) => {
      setFOnline((prev) => { const n = { ...prev }; (ev.joins || []).forEach((p) => { seenRef.current[p.user_id] = Date.now(); devRef.current[p.user_id] = deviceFromStatus(p.status) || devRef.current[p.user_id] || ''; n[p.user_id] = labelFromStatus(p.status) || n[p.user_id] || 'En ligne'; }); return n; });
    });
    return off;
  }, [auth, isLoggedIn]);

  const onlineFriends = friends.filter((f) => fOnline[f.user && (f.user.id || f.user.user_id)]);
  const count = onlineFriends.length;
  useEffect(() => { if (onCount) onCount(count); }, [count, onCount]);

  // Chat instantané en fenêtre FLOTTANTE (pas de navigation vers l'espace membre).
  const openDm = (u) => openChatWindow(u);
  const inviteToGroup = async (u) => {
    const uid = u.user_id || u.id; if (!uid) return;
    let group = myGroup;
    if (!group) {
      const created = await auth.createGroup('Mes amis');
      const gid = created && (created.id || (created.group && created.group.id));
      if (!gid) { setMsg('Impossible de créer un groupe pour l\'instant.'); return; }
      group = { id: gid, name: 'Mes amis' }; setMyGroup(group);
    }
    const ok = await auth.inviteToGroup(group.id, [uid]);
    setMsg(ok ? `✅ ${u.display_name || u.username} ajouté à « ${group.name} ».` : `Échec de l'ajout.`);
    setTimeout(() => setMsg(''), 3000);
  };

  if (!isLoggedIn || count === 0) return null;

  const rows = onlineFriends.map((f) => {
    const u = f.user; const uid = u.id || u.user_id; const label = fOnline[uid] || 'En ligne';
    const away = /inactif|absent/i.test(label); const col = away ? '#f59e0b' : '#22c55e';
    const dev = devRef.current[uid] || '';   // 📱 mobile / 💻 PC
    return (
      <Row key={uid}>
        <span className="topline">
          <span className="dot" style={{ background: col, boxShadow: `0 0 5px ${col}` }} title={label} />
          <span className="nmtxt">{u.display_name || u.username}</span>
          {dev ? <span className="dev" title={dev === '📱' ? 'Sur mobile' : 'Sur PC'}>{dev}</span> : null}
          <LevelBadge userId={uid} />
        </span>
        <span className="actions">
          <Ico title="Profil" onClick={() => setCard({ user_id: uid, username: u.username, display_name: u.display_name, avatar_url: u.avatar_url })}><FiUser /></Ico>
          <Ico title="Message privé" onClick={() => openDm(u)}><FiMessageCircle /></Ico>
          <Ico title="Ajouter à un groupe d'amis" onClick={() => inviteToGroup(u)}><FiUserPlus /></Ico>
        </span>
        <span className="st" style={{ color: col }}>{label}</span>
      </Row>
    );
  });

  // MODE DRAWER (mobile) : section pleine largeur, toujours dépliée, sans languette flottante.
  if (mode === 'drawer') {
    return (
      <Section>
        {card && <ProfileCard user={card} activity={fOnline[card.user_id]} onClose={() => setCard(null)} />}
        <SecHead><FiUserCheck /> Amis en ligne <span style={{ color: '#22c55e' }}>({count})</span></SecHead>
        {msg && <Msg>{msg}</Msg>}
        <List>{rows}</List>
      </Section>
    );
  }

  // MODE FLOTTANT (desktop) : languette + panneau.
  return (
    <Dock>
      {card && <ProfileCard user={card} activity={fOnline[card.user_id]} onClose={() => setCard(null)} />}
      <Bar onClick={() => setOpen((o) => !o)}>
        <span className="n"><span className="dot" /> Mes amis en ligne <span className="b">{count}</span></span>
        {open ? <FiChevronDown /> : <FiChevronUp />}
        {/* Badge d'angle avec le NOMBRE d'amis en ligne, visible avant même d'ouvrir. */}
        {!open && <span className="cnt" title={`${count} ami(s) en ligne`}>{count}</span>}
      </Bar>
      {open && (
        <Panel>
          <Head><span style={{ display: 'inline-flex', alignItems: 'center', gap: '.4rem' }}><FiUserCheck /> Amis en ligne ({count})</span>
            <button onClick={() => setOpen(false)} title="Fermer"><FiX /></button></Head>
          {msg && <Msg>{msg}</Msg>}
          <List>{rows}</List>
        </Panel>
      )}
    </Dock>
  );
}
